// frontend/src/components/CampaignManager/dialogs/CopyDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Lottery } from '../types';

interface Props {
  open: boolean;
  title: string;
  itemName: string;
  currentLotteryId?: number;
  lotteries: Lottery[];
  onClose: () => void;
  onCopy: (targetLotteryId: number) => Promise<void>;
}

export default function CopyDialog({ open, title, itemName, currentLotteryId, lotteries, onClose, onCopy }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedLotteryId, setSelectedLotteryId] = useState<number>(0);
  const [copying, setCopying] = useState(false);

  // Filter out the current lottery
  const availableLotteries = lotteries.filter(l => l.id !== currentLotteryId);

  const handleCopy = async () => {
    if (selectedLotteryId === 0) return;

    setCopying(true);
    try {
      await onCopy(selectedLotteryId);
      onClose();
      setSelectedLotteryId(0);
    } catch (error) {
      console.error('Error copying:', error);
      alert('Fehler beim Kopieren');
    } finally {
      setCopying(false);
    }
  };

  const handleClose = () => {
    if (!copying) {
      setSelectedLotteryId(0);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          <strong>{itemName}</strong> in eine andere Lotterie kopieren.
          Alle Einstellungen bleiben erhalten, nur die Lotterie und WKZ werden angepasst.
        </Typography>

        <FormControl fullWidth required>
          <InputLabel>Ziel-Lotterie</InputLabel>
          <Select
            value={selectedLotteryId}
            onChange={(e) => setSelectedLotteryId(Number(e.target.value))}
            label="Ziel-Lotterie"
            disabled={copying}
          >
            <MenuItem value={0}>-- Bitte wählen --</MenuItem>
            {availableLotteries.map(lottery => (
              <MenuItem key={lottery.id} value={lottery.id}>
                {lottery.name} ({lottery.type} {lottery.year})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={copying}>
          Abbrechen
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={copying || selectedLotteryId === 0}
        >
          {copying ? 'Kopiere...' : 'Kopieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
